import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const STATUS_BADGE = {
  new: "bg-blue-50 text-blue-800 border-blue-200",
  pending_verification: "bg-amber-50 text-amber-900 border-amber-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-rose-50 text-rose-800 border-rose-200",
  incomplete: "bg-slate-100 text-slate-700 border-slate-300",
  suspended: "bg-rose-50 text-rose-800 border-rose-200",
};

export default function Applications() {
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [verified, setVerified] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = filter !== "all" ? { status: filter } : {};
    adminApi.get("/admin/applications", { params }).then((r) => setApps(r.data)).catch(() => {});
  };
  useEffect(load, [filter]);

  const openDetail = (app) => {
    setSelected(app);
    setReason("");
    setNote("");
    setVerified(!!app.official_list_verified);
  };

  const decide = async (action) => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/applications/${selected.id}/decision`, {
        action, reason: reason || null, official_list_verified: verified,
      });
      toast.success(`Application ${action.replace("_", " ")} — email draft generated where applicable`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await adminApi.post(`/admin/applications/${selected.id}/notes`, { note });
      toast.success("Note added");
      setNote("");
      const r = await adminApi.get(`/admin/applications/${selected.id}`);
      setSelected(r.data);
      load();
    } catch (e) {
      toast.error("Could not add note");
    }
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-heading font-bold text-2xl text-foreground" data-testid="admin-applications-title">Candidate applications</h1>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-52 bg-white" data-testid="applications-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All statuses</SelectItem>
            {Object.keys(STATUS_BADGE).map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table data-testid="applications-table">
            <TableHeader>
              <TableRow>
                <TableHead>Candidate</TableHead>
                <TableHead>Office</TableHead>
                <TableHead>Municipality</TableHead>
                <TableHead>Verified</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Received</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {apps.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground text-sm py-8">No applications</TableCell></TableRow>
              )}
              {apps.map((a) => (
                <TableRow key={a.id} className="cursor-pointer" onClick={() => openDetail(a)} data-testid={`application-row-${a.id}`}>
                  <TableCell className="font-medium">{a.public_name}<p className="text-xs text-muted-foreground">{a.email}</p></TableCell>
                  <TableCell className="text-sm">{a.office_label}</TableCell>
                  <TableCell className="text-sm">{a.municipality_name}</TableCell>
                  <TableCell>{a.official_list_verified ? <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">verified</Badge> : <Badge variant="outline">not verified</Badge>}</TableCell>
                  <TableCell><Badge variant="outline" className={STATUS_BADGE[a.status]}>{a.status.replace("_", " ")}</Badge></TableCell>
                  <TableCell className="font-mono text-xs tabular-nums">{a.created_at?.slice(0, 10)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{selected.public_name}</DialogTitle>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <p><span className="text-muted-foreground">Legal name:</span> {selected.legal_name}</p>
                <p><span className="text-muted-foreground">Email:</span> {selected.email}</p>
                <p><span className="text-muted-foreground">Phone:</span> {selected.phone || "—"}</p>
                <p><span className="text-muted-foreground">Office:</span> {selected.office_label}</p>
                <p><span className="text-muted-foreground">Website:</span> {selected.campaign_website || "—"}</p>
                <p><span className="text-muted-foreground">Ballot position:</span> {selected.paper_ballot_position}</p>
              </div>
              {selected.official_candidate_list_url && (
                <p className="text-sm"><span className="text-muted-foreground">Official list link:</span>{" "}
                  <a href={selected.official_candidate_list_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">{selected.official_candidate_list_url}</a>
                </p>
              )}
              {selected.why_join && <p className="text-sm text-muted-foreground bg-slate-50 border border-slate-200 rounded-lg p-3">{selected.why_join}</p>}
              {selected.decision && (
                <p className="text-xs text-muted-foreground">Decision by {selected.decision.by} at {selected.decision.at}: {selected.decision.reason}</p>
              )}

              <Separator />
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={verified} onCheckedChange={(v) => setVerified(!!v)} className="mt-0.5" data-testid="application-verified-checkbox" />
                <span className="text-sm">Verified against the official candidate list (required before approval)</span>
              </label>
              <div>
                <Label>Reason / requested information (required for reject, request info, suspend)</Label>
                <Textarea className="mt-1.5" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="application-decision-reason" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => decide("approve")} disabled={busy || !verified} data-testid="application-approve-button">Approve</Button>
                <Button variant="outline" onClick={() => decide("request_info")} disabled={busy || !reason} data-testid="application-request-info-button">Request info</Button>
                <Button variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => decide("reject")} disabled={busy || !reason} data-testid="application-reject-button">Reject</Button>
                {selected.status !== "suspended" ? (
                  <Button variant="outline" onClick={() => decide("suspend")} disabled={busy || !reason} data-testid="application-suspend-button">Suspend</Button>
                ) : (
                  <Button variant="outline" onClick={() => decide("restore")} disabled={busy} data-testid="application-restore-button">Restore</Button>
                )}
              </div>

              <Separator />
              <div>
                <h3 className="font-heading font-semibold text-sm mb-2">Admin notes</h3>
                {(selected.admin_notes || []).length === 0 && <p className="text-xs text-muted-foreground">No notes yet.</p>}
                <ul className="space-y-2">
                  {(selected.admin_notes || []).map((n, i) => (
                    <li key={i} className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                      <span className="font-medium">{n.by}</span> · <span className="font-mono">{n.at?.slice(0, 16)}</span>
                      <p className="mt-1">{n.note}</p>
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2 mt-3">
                  <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" data-testid="application-note-input" />
                  <Button variant="outline" onClick={addNote} data-testid="application-add-note-button">Add</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
